import { useState, type ReactNode } from 'react'
import type { Vehicle } from '../../data/vehicles'
import { assetUrl } from '../../lib/http'

/* A car's picture, and what stands in for it until one is taken.

   Both card shapes on this site show a car the same way — the editorial card
   in the rails and the compact card in the inventory grid — so the photograph,
   its failure case and the silhouette behind it live here rather than being
   written twice.

   The element fills its parent, which owns the aspect ratio. */

/* A stylised side profile — the placeholder until photography lands. One
   generic saloon rather than a shape per body: at this level of abstraction
   the profiles are near enough indistinguishable, and the card no longer
   knows what shape of car it is drawing. */
const SILHOUETTE = {
  body: 'M16 82 L16 60 C16 53 20 49 27 47 L76 36 C86 22 99 15 116 15 L154 15 C171 15 183 20 191 33 L237 46 C244 48 248 52 248 59 L248 82 Z',
  glass: 'M84 38 C92 27 103 22 117 22 L152 22 C165 22 175 27 182 37 L182 38 Z',
  pillar: 134,
  wheels: [66, 198],
  radius: 18,
}

/* The ground a car stands on, and the light falling on it.

   The photographs the showroom files are cut-outs — a car on transparency,
   which is what a dealer's product shot is — so a plate behind one is not a
   background, it is a black box with a car floating in the middle of it. The
   plate is gone: the media sits flush on the card it is part of, lit from
   above, with a pool of floor light under the sills.

   That pool is what makes the shadow work. A shadow on a near-black card is
   invisible, so the floor is lit first and the car's own shadow falls across
   it — the same reason a studio lights the sweep before it shoots the car.

   Exported because the car's own page frames the same photograph, and one
   showroom should not have two floors. */
export function Stage({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* The key light, from above and slightly behind. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(70%_100%_at_50%_0%,rgb(255_255_255/0.06),transparent)]"
      />
      {/* The floor, in two washes. The wide one is the lit sweep the car
          stands on — without it the shadow below has nothing to fall across
          and a near-black card simply swallows it. The tighter one is the
          showroom's own lime, the pool the silhouette has always stood in,
          so a car waiting for its photograph and a car that has one are lit
          by the same room. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-[4%] bottom-0 h-[38%] rounded-[50%] bg-[radial-gradient(closest-side,rgb(255_255_255/0.10),transparent)]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-x-[16%] bottom-[3%] h-[20%] rounded-[50%] bg-[radial-gradient(closest-side,rgb(200_245_66/0.22),transparent)]"
      />
      {children}
    </div>
  )
}

/** How a car's photograph is lit wherever it is framed: contained rather
    than cropped, and carrying its own shadow.

    The shadow is a filter rather than a drawn ellipse. A filter is cast by
    the alpha channel, so it is the shape of this car and falls under this
    car's wheels whatever margins the photograph was saved with — where an
    ellipse at a fixed offset is a guess that is wrong on the next photograph
    somebody uploads. Two of them: a tight contact under the tyres, and a
    wider ambient one that gives the car its weight. */
export const CAR_IMAGE =
  'object-contain [filter:drop-shadow(0_4px_3px_rgb(0_0_0/0.8))_drop-shadow(0_13px_10px_rgb(0_0_0/0.6))]'

/* The card media. A remote photo can fail — a hotlink can rot, a phone can
   be offline — so the silhouette stays in the file as the fallback rather
   than leaving a torn image icon in the grid. */
function VehicleMedia({
  vehicle,
  source,
}: {
  vehicle: Vehicle
  source: string
}) {
  const [failed, setFailed] = useState(false)

  /* Resolved against the API's host: a photograph the console uploaded is a
     path there, not on the storefront. */
  const photo = source ? assetUrl(source) : ''

  /* A failure belongs to the photograph rather than to the card: the grid
     refetches and re-sorts under these, so a card handed a different car must
     not keep the last one's broken state and show a silhouette for it. */
  const [shown, setShown] = useState(source)
  if (shown !== source) {
    setShown(source)
    setFailed(false)
  }

  if (!photo || failed) {
    return (
      <Stage>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <CarSilhouette uid={vehicle.id} />
        </div>
      </Stage>
    )
  }

  return (
    <Stage>
      {/* Contained rather than cropped, and deliberately: a cut-out filled to
          the frame loses its wheels and its roof, which are most of what tells
          one car from another at this size. The file's own margins are what
          keep it off the edges, so nothing is padded in here. */}
      <img
        src={photo}
        alt=""
        loading="lazy"
        decoding="async"
        width={1600}
        height={1000}
        onError={() => setFailed(true)}
        className={`absolute inset-0 size-full ${CAR_IMAGE} transition duration-700 ease-lux group-hover:scale-[1.06]`}
      />
    </Stage>
  )
}

/* Gradient ids must be unique per instance — several of these render at once. */
export function CarSilhouette({ uid }: { uid: string }) {
  const { body: shape, glass, pillar, wheels, radius } = SILHOUETTE
  const paint = `paint-${uid}`
  const tint = `tint-${uid}`
  const rim = `rim-${uid}`

  return (
    <svg
      viewBox="0 0 264 104"
      aria-hidden="true"
      className="relative w-[86%] max-w-[300px] drop-shadow-[0_12px_20px_rgba(0,0,0,0.45)] transition-transform duration-500 ease-lux group-hover:scale-[1.06]"
    >
      <defs>
        {/* Dark body with a lit shoulder — on the night ground it is the
            highlight running along the roofline that draws the car, the way
            studio photography of a black car works. */}
        <linearGradient id={paint} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e8e8e8" />
          <stop offset="24%" stopColor="#8c8c8c" />
          <stop offset="58%" stopColor="#3a3a3a" />
          <stop offset="100%" stopColor="#181818" />
        </linearGradient>
        <linearGradient id={tint} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor="#c8f542" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id={rim} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#d2d2d2" />
          <stop offset="100%" stopColor="#3f3f3f" />
        </linearGradient>
      </defs>

      <ellipse cx="132" cy="99" rx="114" ry="5" fill="#000000" opacity="0.4" />
      <path d={shape} fill={`url(#${paint})`} />
      <path d={glass} fill={`url(#${tint})`} />
      <line
        x1={pillar}
        y1="22"
        x2={pillar}
        y2="38"
        stroke="#3a3a3a"
        strokeWidth="2.5"
      />
      {wheels.map((cx) => (
        <g key={cx}>
          <circle
            cx={cx}
            cy="80"
            r={radius}
            fill="#0d0d0d"
            stroke="#2c2c2c"
            strokeWidth="1"
          />
          <circle cx={cx} cy="80" r={radius - 7} fill={`url(#${rim})`} />
          <circle cx={cx} cy="80" r="2.5" fill="#161616" />
        </g>
      ))}
    </svg>
  )
}

export default VehicleMedia
